import Papa from "papaparse";
import { apiClient } from "module/api";

export interface ParsedVariableCsv {
  columns: string[];
  firstRow: string[];
  totalRows: number;
}

export interface UploadedVariable {
  id: string;
  name: string;
  columns: string[];
  rowsAmount: number;
}

export const parseVariableCsv = (file: File): Promise<ParsedVariableCsv> =>
  new Promise((resolve, reject) => {
    Papa.parse<string[]>(file, {
      skipEmptyLines: true,
      complete: (result) => {
        const rows = result.data;
        if (!rows.length || !rows[0]?.length) {
          reject(new Error("The file is empty or has no columns."));
          return;
        }
        const columns = rows[0].map((cell) => String(cell).trim()).filter(Boolean);
        resolve({ columns, firstRow: rows[1] ?? [], totalRows: Math.max(rows.length - 1, 0) });
      },
      error: (error) => reject(error),
    });
  });

export const uploadVariableFile = async ({
  clientId,
  file,
  parsed,
}: {
  clientId: string;
  file: File;
  parsed: ParsedVariableCsv;
}): Promise<UploadedVariable> => {
  const presignedRes = await apiClient.getVariablePresignedUrl({ fileName: file.name, clientId: Number(clientId) });
  if (!presignedRes.ok) throw new Error(`Failed to get presigned URL: ${presignedRes.status}`);
  const { url: presignedUrl, dbURL } = await presignedRes.json();

  const rawBlob = new Blob([await file.arrayBuffer()]);
  const s3Res = await fetch(presignedUrl, { method: "PUT", body: rawBlob });
  if (!s3Res.ok) throw new Error(`Failed to upload file to S3: ${s3Res.status}`);

  const columnMappings = parsed.columns.map((column) => ({ originalName: column, variableName: column }));
  const createRes = await apiClient.createVariable({
    name: file.name.replace(/\.(csv|xlsx?)$/i, ""),
    csv_url: dbURL,
    csv_columns: parsed.columns,
    client_id: Number(clientId),
    metadata: {
      headers: parsed.columns,
      firstRow: parsed.firstRow,
      rowsAmount: parsed.totalRows,
      fileUrl: dbURL,
      columnMappings,
    },
  });
  if (!createRes.ok) throw new Error(`Failed to create variable record: ${createRes.status}`);
  const created = await createRes.json();

  return {
    id: String(created.id),
    name: created.name,
    columns: created.csv_columns ?? parsed.columns,
    rowsAmount: created.metadata?.rowsAmount ?? parsed.totalRows,
  };
};
